import { useEffect, useState } from "react";
import { ArrowDownToLine, Check, X, Loader2, ExternalLink } from "lucide-react";
import { api, formatErr, cx } from "../lib/api";
import { useFx } from "../lib/fx";

const FILTERS = [
  { id: "pending", label: "Pending" },
  { id: "approved", label: "Approved" },
  { id: "paid", label: "Paid" },
  { id: "rejected", label: "Rejected" },
  { id: "all", label: "All" },
];

export default function AdminWithdrawalsPanel() {
  const { format } = useFx();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);

  const reload = async () => {
    try {
      const { data } = await api.get(`/admin/withdrawals?status=${filter}`);
      setItems(data.items || []);
    } catch (e) { setError(formatErr(e)); }
  };

  useEffect(() => { reload(); }, [filter]);

  const act = async (w, action) => {
    let admin_note = null;
    let tx_reference = null;
    if (action === "reject") {
      admin_note = window.prompt("Reason for rejection (will be visible to user):", "");
      if (admin_note === null) return;
    } else if (action === "mark_paid") {
      tx_reference = window.prompt("Transaction reference / hash:", "");
      if (tx_reference === null) return;
    } else if (action === "approve") {
      if (!window.confirm(`Approve withdrawal of ${w.amount} ${w.currency} to ${w.destination_address}?`)) return;
    }
    setBusy(w.id); setError(null);
    try {
      await api.post(`/admin/withdrawals/${w.id}`, { action, admin_note, tx_reference });
      reload();
    } catch (e) { setError(formatErr(e)); }
    finally { setBusy(null); }
  };

  return (
    <div data-testid="admin-withdrawals-panel">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
        <div>
          <div className="eyebrow flex items-center gap-2 text-teal"><ArrowDownToLine size={12}/> Cash-out</div>
          <h2 className="font-serif-display text-3xl mt-1">Withdrawal requests</h2>
          <p className="text-sm text-muted mt-1">Approve, reject, or mark paid (after sending the actual funds out).</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {FILTERS.map((f) => (
            <button key={f.id} onClick={() => setFilter(f.id)} className={cx("tab-pill", filter === f.id && "active")} data-testid={`wd-filter-${f.id}`}>
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {error && <div className="text-rose-300 text-sm mb-3">{error}</div>}

      {items.length === 0 ? (
        <div className="luxe-card p-10 text-center text-muted text-sm">No withdrawals match this filter.</div>
      ) : (
        <div className="space-y-3">
          {items.map((w) => (
            <div key={w.id} className="luxe-card p-5 flex flex-wrap items-start justify-between gap-3" data-testid={`adw-${w.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className={cx("chip",
                    w.status === "paid" ? "success" :
                    w.status === "approved" ? "info" :
                    w.status === "rejected" ? "danger" : "warning")}>
                    {w.status}
                  </span>
                  <span className="text-xs text-muted font-mono">#{w.id.slice(0,8)}</span>
                  <span className="text-xs text-muted">{new Date(w.created_at).toLocaleString()}</span>
                </div>
                <div className="font-serif-display text-2xl">
                  {format(w.amount, w.currency, { native: true })}
                  <span className="text-base text-muted"> → ~{format(w.estimated_payout, w.destination_currency, { native: true })}</span>
                </div>
                <div className="text-xs text-muted mt-1">
                  {w.user_name || w.user_email} · <span className="font-mono">{w.destination_kind}</span> ·
                  <span className="font-mono ml-1">{w.destination_address.slice(0, 36)}{w.destination_address.length > 36 ? "…" : ""}</span>
                </div>
                {w.note && <div className="text-xs text-muted mt-1 italic">"{w.note}"</div>}
                {w.admin_note && <div className="text-xs text-teal/80 mt-1">admin: "{w.admin_note}"</div>}
                {w.tx_reference && <div className="text-xs text-teal/80 mt-1 font-mono flex items-center gap-1"><ExternalLink size={10}/> {w.tx_reference}</div>}
              </div>
              <div className="flex flex-wrap gap-2 shrink-0">
                {w.status === "pending" && (
                  <button onClick={() => act(w, "approve")} disabled={busy === w.id} className="btn btn-outline text-[12px]" data-testid={`adw-approve-${w.id}`}>
                    <Check size={12}/> Approve
                  </button>
                )}
                {(w.status === "approved" || w.status === "pending") && (
                  <button onClick={() => act(w, "mark_paid")} disabled={busy === w.id} className="btn btn-primary text-[12px]" data-testid={`adw-paid-${w.id}`}>
                    {busy === w.id ? <Loader2 size={12} className="animate-spin"/> : <ArrowDownToLine size={12}/>}
                    Mark paid
                  </button>
                )}
                {(w.status === "pending" || w.status === "approved") && (
                  <button onClick={() => act(w, "reject")} disabled={busy === w.id} className="btn btn-ghost text-[12px] !text-rose-300" data-testid={`adw-reject-${w.id}`}>
                    <X size={12}/> Reject
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
